//
// ISOMORPHIC — this file is byte-identical to
// client/src/cognitive-flows/components/canvas/chartResolve.js, and
// tests/unit/server/cognitive-flows/canvasChartResolveParity.test.js fails
// the moment the two drift. No imports, no Node or DOM APIs: it runs in the
// browser (card, settings panel, exports) and on the server (the scheduled
// email's chart-as-table fallback).
//
// What it does: a block's chart settings (block.config.chartSettings[i]) are
// an OVERLAY on the chart a run produced. resolveChart() applies that overlay
// — deriving new series from the result table when the reader picked columns,
// filling a complete `look` — and reports whatever it could not do as issues.
// Nothing here mutates its inputs. See
// docs/superpowers/specs/2026-08-27-canvas-chart-settings-design.md.

export const CHART_SETTINGS_LIMITS = Object.freeze({
    MAX_CHARTS: 4, MAX_SERIES: 8, MAX_NAME: 120, MAX_TITLE: 200,
    MAX_TOP_N: 50, MAX_DECIMALS: 4, MAX_POINTS: 200, MAX_CURRENCY: 4,
});
export const LOOK_TYPES = Object.freeze(["bar", "line", "area", "pie", "donut", "combo"]);
export const SERIES_AGGS = Object.freeze(["none", "sum", "avg", "count", "min", "max"]);
export const SERIES_KINDS = Object.freeze(["bar", "line"]);
export const SORTS = Object.freeze(["asis", "label", "value_desc", "value_asc"]);
export const CURVES = Object.freeze(["monotone", "linear", "step"]);
export const LEGEND_POSITIONS = Object.freeze(["bottom", "top", "right"]);
export const TICK_ANGLES = Object.freeze([0, -45, -90]);
export const FORMAT_KINDS = Object.freeze(["plain", "thousands", "compact", "percent", "currency"]);

// Every palette was run through the dataviz skill's validate_palette.js
// (lightness band, chroma floor, adjacent-pair CVD ΔE, normal-vision floor,
// contrast) against a white surface. `default` is the renderer's historical
// SERIES_COLORS, kept so a chart nobody restyled does not change. `mono` is a
// 5-step ordinal ramp — more steps fail the adjacent-ΔL check — and a series
// past its end takes the `safe` slot of the same index, never a re-cycled hue.
export const CHART_PALETTES = Object.freeze({
    default: ["#3b82f6", "#f59e0b", "#10b981", "#a855f7", "#ef4444", "#06b6d4", "#84cc16", "#ec4899"],
    safe: ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"],
    cool: ["#2a78d6", "#1baf7a", "#4a3aa7", "#06b6d4", "#008300", "#9085e9", "#256abf", "#199e70"],
    warm: ["#d95926", "#e87ba4", "#eda100", "#9085e9", "#e34948", "#c98500", "#4a3aa7", "#eb6834"],
    mono: ["#104281", "#1c5cab", "#2a78d6", "#5598e7", "#86b6ef"],
});
export const PALETTE_NAMES = Object.freeze(Object.keys(CHART_PALETTES));

const HEX_RE = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
export function isHex(v) {
    return typeof v === "string" && HEX_RE.test(v);
}

const oneOf = (list, v, fallback) => (list.includes(v) ? v : fallback);
const clampInt = (v, lo, hi, fallback) => {
    const n = Number(v);
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : fallback;
};
const finiteOrNull = (v) => {
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};
const str = (v, max) => (typeof v === "string" ? v.trim().slice(0, max) : "");

/** The chart_type vocabulary every consumer already branches on. */
export function legacyChartType(lookType) {
    if (lookType === "pie" || lookType === "donut") return "pie";
    if (lookType === "line" || lookType === "area") return "line";
    return "bar";
}

/**
 * A complete look — every field present — from a possibly-sparse one.
 *
 * `opts.showLegend` is the legend's fallback when the reader never touched
 * it: the caller passes the STORED result's own `show_legend` (default
 * `true` when omitted) so an absent `look.legend.show` falls through to
 * whatever the chart already had, rather than silently turning a hidden
 * legend back on. `opts.currency` is the ACCOUNT's symbol: it fills
 * `format.currency` when the reader never set one (a stored override wins),
 * falling back to "$" when the account has none.
 */
export function defaultLook(raw, baseType, opts) {
    const o = opts && typeof opts === "object" ? opts : {};
    const l = raw && typeof raw === "object" ? raw : {};
    const type = oneOf(LOOK_TYPES, l.type, oneOf(["bar", "line", "pie"], baseType, "bar"));
    const legend = l.legend && typeof l.legend === "object" ? l.legend : {};
    const format = l.format && typeof l.format === "object" ? l.format : {};
    const pie = l.pie && typeof l.pie === "object" ? l.pie : {};
    let yMin = finiteOrNull(l.yMin);
    let yMax = finiteOrNull(l.yMax);
    if (yMin !== null && yMax !== null && yMin >= yMax) { yMin = null; yMax = null; }
    const donut = Number(pie.donutWidth);
    return {
        type,
        horizontal: type === "bar" && l.horizontal === true,
        stacked: (type === "bar" || type === "area") && l.stacked === true,
        curve: oneOf(CURVES, l.curve, "monotone"),
        dots: l.dots === true,
        connectNulls: l.connectNulls === true,
        palette: oneOf(PALETTE_NAMES, l.palette, "default"),
        colors: Array.isArray(l.colors)
            ? l.colors.slice(0, CHART_SETTINGS_LIMITS.MAX_SERIES).map((c) => (isHex(c) ? c : null))
            : [],
        title: str(l.title, CHART_SETTINGS_LIMITS.MAX_TITLE),
        xLabel: str(l.xLabel, CHART_SETTINGS_LIMITS.MAX_NAME),
        yLabel: str(l.yLabel, CHART_SETTINGS_LIMITS.MAX_NAME),
        legend: {
            show: legend.show !== undefined ? legend.show !== false : o.showLegend !== false,
            position: oneOf(LEGEND_POSITIONS, legend.position, "bottom"),
        },
        // A pie/donut is ALWAYS labelled by the legacy renderer (PieBlock
        // passes a bare `label`), so an untouched pie must stay labelled —
        // only an explicit `dataLabels: false` turns them off. Every other
        // type keeps the old opt-in default.
        dataLabels: l.dataLabels === undefined ? (type === "pie" || type === "donut") : l.dataLabels === true,
        grid: l.grid !== false,
        xTickAngle: TICK_ANGLES.includes(l.xTickAngle) ? l.xTickAngle : 0,
        yMin,
        yMax,
        format: {
            kind: oneOf(FORMAT_KINDS, format.kind, "plain"),
            decimals: format.decimals === null || format.decimals === undefined
                ? null
                : clampInt(format.decimals, 0, CHART_SETTINGS_LIMITS.MAX_DECIMALS, null),
            currency: str(format.currency, CHART_SETTINGS_LIMITS.MAX_CURRENCY) || str(o.currency, CHART_SETTINGS_LIMITS.MAX_CURRENCY) || "$",
        },
        pie: {
            percentLabels: pie.percentLabels === true,
            donutWidth: Number.isFinite(donut) ? Math.min(0.9, Math.max(0.4, donut)) : 0.6,
        },
    };
}

/** One hex per slot: the explicit override, then the palette, then `safe` past a short palette. */
export function seriesColors(look, count) {
    const pal = CHART_PALETTES[look.palette] || CHART_PALETTES.default;
    const out = [];
    for (let i = 0; i < count; i++) {
        out.push(look.colors[i] || pal[i] || CHART_PALETTES.safe[i] || CHART_PALETTES.default[i % CHART_PALETTES.default.length]);
    }
    return out;
}

const trimZeros = (s) => (s.includes(".") ? s.replace(/\.?0+$/, "") : s);
const grouped = (n, decimals) => n.toLocaleString("en-US", {
    minimumFractionDigits: decimals === null ? 0 : decimals,
    maximumFractionDigits: decimals === null ? 2 : decimals,
});

/**
 * A number for an axis tick, a tooltip, a data label or an email cell.
 * `percent` does NOT multiply — the value is already a percentage.
 */
export function formatNumber(v, format) {
    if (v === null || v === undefined || v === "") return "";
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n)) return "";
    const f = format && typeof format === "object" ? format : {};
    const kind = oneOf(FORMAT_KINDS, f.kind, "plain");
    const d = f.decimals === null || f.decimals === undefined
        ? null
        : clampInt(f.decimals, 0, CHART_SETTINGS_LIMITS.MAX_DECIMALS, null);
    if (kind === "plain") return d === null ? String(Math.round(n * 100) / 100) : n.toFixed(d);
    if (kind === "thousands") return grouped(n, d);
    if (kind === "percent") return `${d === null ? trimZeros(n.toFixed(1)) : n.toFixed(d)}%`;
    if (kind === "currency") {
        const sym = str(f.currency, CHART_SETTINGS_LIMITS.MAX_CURRENCY) || "$";
        return `${n < 0 ? "-" : ""}${sym}${grouped(Math.abs(n), d === null ? 2 : d)}`;
    }
    // compact
    const abs = Math.abs(n);
    const unit = abs >= 1e9 ? [1e9, "B"] : abs >= 1e6 ? [1e6, "M"] : abs >= 1e3 ? [1e3, "k"] : null;
    if (!unit) return d === null ? String(Math.round(n * 100) / 100) : n.toFixed(d);
    const scaled = n / unit[0];
    return `${d === null ? trimZeros(scaled.toFixed(1)) : scaled.toFixed(d)}${unit[1]}`;
}

// ── Derivation ───────────────────────────────────────────────────────────────

/** "1,234.5" → 1234.5; 7 → 7; "" / "abc" / null → null. Mirrors canvasBlockShapers.coerceNumber. */
function coerceNumber(v) {
    if (typeof v === "number") return Number.isFinite(v) ? v : null;
    if (typeof v !== "string") return null;
    const n = Number(v.replace(/,/g, "").trim());
    return v.trim() !== "" && Number.isFinite(n) ? n : null;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;
const MDY_DATE = /^\d{1,2}\/\d{1,2}\/\d{4}/;

/** A label as a sortable instant, or null when it isn't a date. Mirrors canvasBlockShapers.asDateKey. */
function asDateKey(v) {
    const s = String(v ?? "");
    if (ISO_DATE.test(s)) return Date.parse(s.slice(0, 10));
    if (MDY_DATE.test(s)) {
        const [m, d, y] = s.split("/");
        return Date.parse(`${y}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`);
    }
    return null;
}

const AGG = {
    sum: (nums) => nums.reduce((a, b) => a + b, 0),
    avg: (nums) => nums.reduce((a, b) => a + b, 0) / nums.length,
    min: (nums) => Math.min(...nums),
    max: (nums) => Math.max(...nums),
};
const round2 = (v) => (v === null || v === undefined ? null : Math.round(v * 100) / 100);
const MANY_GROUPS = 20;

const issue = (code, severity, message, fix) => (fix ? { code, severity, message, fix } : { code, severity, message });

/** A chart's series in one shape, whether it stores one or several. */
function seriesOf(cd) {
    const raw = Array.isArray(cd?.chart_series) ? cd.chart_series.filter((s) => s && Array.isArray(s.points)) : [];
    if (raw.length >= 2) return raw.map((s, i) => ({ name: String(s.name || `Series ${i + 1}`), points: s.points }));
    const yRaw = Array.isArray(cd?.chart_y_axis_data_points) ? cd.chart_y_axis_data_points : [];
    return [{ name: String(cd?.series_name || "Value"), points: Array.isArray(yRaw[0]) ? yRaw[0] : yRaw }];
}

/**
 * x + series from the table, per `data`. Returns { x, series, issues }, or
 * { issues } alone on a fatal problem — the caller then falls back to the base.
 */
function deriveFromTable(data, rows, lookType) {
    const issues = [];
    if (!Array.isArray(rows) || rows.length === 0) {
        return { issues: [issue("NO_TABLE", "error", "This run produced no table to chart from — showing the agent's chart.", "reset")] };
    }
    const keys = new Set(rows.flatMap((r) => (r && typeof r === "object" ? Object.keys(r) : [])));
    if (!keys.has(data.x)) {
        return { issues: [issue("X_MISSING", "error", `Column "${data.x}" is not in this run's table — showing the agent's chart.`, "reset")] };
    }
    const wanted = Array.isArray(data.series) ? data.series.filter((s) => s && s.column) : [];
    const kept = [];
    for (const sr of wanted) {
        if (!keys.has(sr.column)) {
            issues.push(issue("SERIES_MISSING", "warn", `Column "${sr.column}" is not in this run's table — that series is left out.`));
            continue;
        }
        const agg = SERIES_AGGS.includes(sr.agg) ? sr.agg : "none";
        const numeric = rows.some((r) => coerceNumber(r?.[sr.column]) !== null);
        if (!numeric && agg !== "count") {
            issues.push(issue("SERIES_NOT_NUMERIC", "warn", `"${sr.column}" has no numeric values — that series is left out.`));
            continue;
        }
        kept.push({ ...sr, agg });
    }
    if (kept.length === 0) {
        issues.push(issue("NO_SERIES", "error", "None of the chosen columns can be plotted — showing the agent's chart.", "reset"));
        return { issues };
    }

    // Group by x, first-seen order.
    const groups = new Map();
    for (const r of rows) {
        const k = String(r?.[data.x] ?? "");
        if (!groups.has(k)) groups.set(k, []);
        groups.get(k).push(r);
    }
    const repeats = [...groups.values()].some((g) => g.length > 1);
    if (repeats && kept.some((s) => s.agg === "none")) {
        issues.push(issue("X_REPEATS", "info", `"${data.x}" repeats across rows — repeated values are summed. Pick an aggregation to change that.`, "pickAgg"));
    }
    const valueOf = (sr, g) => {
        if (sr.agg === "count") return g.length;
        const nums = g.map((r) => coerceNumber(r?.[sr.column])).filter((n) => n !== null);
        if (nums.length === 0) return null;
        return AGG[sr.agg === "none" ? "sum" : sr.agg](nums);
    };
    let points = [...groups.entries()].map(([k, g]) => ({ x: k, values: kept.map((sr) => valueOf(sr, g)) }));

    // Sort.
    const sort = SORTS.includes(data.sort) ? data.sort : "asis";
    if (sort === "label") {
        const allDates = points.every((p) => asDateKey(p.x) !== null);
        points.sort((a, b) => (allDates ? asDateKey(a.x) - asDateKey(b.x) : (a.x < b.x ? -1 : a.x > b.x ? 1 : 0)));
    } else if (sort === "value_desc" || sort === "value_asc") {
        const v = (p) => (p.values[0] === null ? -Infinity : p.values[0]);
        points.sort((a, b) => (sort === "value_desc" ? v(b) - v(a) : v(a) - v(b)));
    }

    // Top-N and the Other bucket.
    const topN = clampInt(data.topN, 0, CHART_SETTINGS_LIMITS.MAX_TOP_N, 0);
    if (topN > 0 && points.length > topN) {
        const rest = points.slice(topN);
        points = points.slice(0, topN);
        if (data.other === true) {
            const bucketable = kept.every((s) => s.agg === "sum" || s.agg === "count" || s.agg === "none");
            if (bucketable) {
                // A real category named "Other" that survived the cut would
                // otherwise share its label with the synthetic bucket.
                const otherLabel = points.some((p) => p.x === "Other") ? "Other (rest)" : "Other";
                points.push({ x: otherLabel, values: kept.map((_, i) => rest.reduce((sum, p) => sum + (p.values[i] ?? 0), 0)) });
            } else {
                issues.push(issue("OTHER_NOT_APPLICABLE", "info", "\"Other\" only makes sense for summed or counted series, so no bucket was added."));
            }
        }
    } else if (topN === 0 && points.length > MANY_GROUPS) {
        issues.push(issue("MANY_GROUPS", "info", `${points.length} categories — consider a Top N.`, "pickTopN"));
    }

    // Pie rules.
    if (lookType === "pie" || lookType === "donut") {
        if (kept.length > 1) issues.push(issue("PIE_SINGLE_SERIES", "warn", "A pie shows one series — only the first is used."));
        const before = points.length;
        points = points.filter((p) => p.values[0] !== null && p.values[0] >= 0);
        if (points.length < before) {
            issues.push(issue("NEGATIVE_DROPPED", "warn", `${before - points.length} value(s) that are missing or negative cannot be pie slices and were left out.`));
        }
    }
    if (points.length === 0) {
        issues.push(issue("EMPTY", "error", "The chosen columns produce no points — showing the agent's chart.", "reset"));
        return { issues };
    }
    if (points.length > CHART_SETTINGS_LIMITS.MAX_POINTS) {
        issues.push(issue("POINTS_CAPPED", "info", `Showing the first ${CHART_SETTINGS_LIMITS.MAX_POINTS} of ${points.length} categories — pick a Top N to choose which.`, "pickTopN"));
    }
    points = points.slice(0, CHART_SETTINGS_LIMITS.MAX_POINTS);
    return {
        x: points.map((p) => p.x),
        series: kept.map((sr, i) => ({
            name: String(sr.label || sr.column),
            kind: SERIES_KINDS.includes(sr.kind) ? sr.kind : "bar",
            points: points.map((p) => round2(p.values[i])),
        })),
        issues,
    };
}

/**
 * The reader's overlay applied to a run's chart.
 *
 * No settings ⇒ the SAME chartData object back, untouched and without a
 * `look` — the renderer's legacy branch then draws exactly what it always did.
 * Otherwise the result keeps the Chart_data contract (chart_type in the legacy
 * bar|line|pie vocabulary) and carries a complete `look`, whose `colors` is
 * one hex per series — or per SLICE for a pie/donut — and whose
 * `seriesKinds` says bar-or-line per series for a combo.
 */
export function resolveChart(chartData, tableRows, settings, opts) {
    const base = chartData && typeof chartData === "object" ? chartData : null;
    const s = settings && typeof settings === "object" ? settings : null;
    if (!s || (!s.data && !s.look)) return { chart: base, issues: [] };

    const look = defaultLook(s.look, base?.chart_type, { showLegend: base?.show_legend !== false, currency: opts?.currency });
    const isPie = look.type === "pie" || look.type === "donut";
    let issues = [];
    let x = null;
    let series = null;
    let derived = false;

    const data = s.data && typeof s.data === "object" && s.data.x ? s.data : null;
    if (data) {
        const d = deriveFromTable(data, tableRows, look.type);
        issues = d.issues;
        if (d.series) { x = d.x; series = d.series; derived = true; }
    }
    if (!derived) {
        if (!base) return { chart: null, issues };
        x = Array.isArray(base.charx_axis_data_points) ? base.charx_axis_data_points : [];
        series = seriesOf(base).map((sr) => ({ ...sr, kind: "bar" }));
        if (isPie && series.length > 1) issues.push(issue("PIE_SINGLE_SERIES", "warn", "A pie shows one series — only the first is used."));
    }
    if (isPie) {
        series = series.slice(0, 1);
        // A pie draws only non-null, non-negative slices, and `colors` must be
        // sized on exactly those. The derived branch already filtered; a
        // restyled agent chart is filtered here by the same rule.
        if (!derived) {
            const pts = series[0].points;
            const keep = x.map((_, i) => {
                const v = pts[i];
                const n = typeof v === "number" ? v : Number(v);
                return v !== null && v !== undefined && Number.isFinite(n) && n >= 0;
            });
            const dropped = keep.filter((k) => !k).length;
            if (dropped > 0) {
                issues.push(issue("NEGATIVE_DROPPED", "warn", `${dropped} value(s) that are missing or negative cannot be pie slices and were left out.`));
                x = x.filter((_, i) => keep[i]);
                series = series.map((sr) => ({ ...sr, points: sr.points.filter((_, i) => keep[i]) }));
            }
        }
    }

    const colors = seriesColors(look, isPie ? x.length : series.length);
    const chart = {
        ...(base || {}),
        chart_type: legacyChartType(look.type),
        chart_header: look.title || base?.chart_header || (derived ? `${series[0].name} by ${data.x}` : "Chart"),
        charx_axis_data_points: x,
        chart_y_axis_data_points: series[0].points,
        show_legend: look.legend.show,
        look: { ...look, colors, seriesKinds: series.map((sr) => sr.kind) },
    };
    if (series.length > 1) chart.chart_series = series.map(({ name, points }) => ({ name, points }));
    else delete chart.chart_series;
    if (derived) chart.series_name = series[0].name;
    if (look.xLabel) chart.x_axis_label = look.xLabel;
    if (look.yLabel) chart.y_axis_label = look.yLabel;
    return { chart, issues };
}

// ── Whole-block resolution ───────────────────────────────────────────────────

/** Block types whose result carries the agent viz shape ({ viz: { Chart_data/charts/table } }). */
const AGENT_SHAPED_TYPES = new Set(["agent", "workflow"]);

/** The charts a block's result stores: `charts` beyond one, else `Chart_data` alone. */
export function baseChartsOf(block) {
    if (!block || !block.result || block.result.error) return [];
    if (block.type === "chart") return block.result.Chart_data ? [block.result.Chart_data] : [];
    if (!AGENT_SHAPED_TYPES.has(block.type)) return [];
    const viz = block.result.viz;
    return Array.isArray(viz?.charts) && viz.charts.length > 1
        ? viz.charts
        : (viz?.Chart_data ? [viz.Chart_data] : []);
}

/**
 * Every chart of a block, resolved: [{ index, chart, issues, settings, table }].
 *
 * Index-aligned with baseChartsOf(): settings[i] applies to chart i. An agent
 * block with NO chart but a table gains chart 0 when settings[0].data asks for
 * one — the reader charting a table-only answer. A settings entry past the
 * charts the run produced is ignored, never deleted. `chart` is null when a
 * derivation failed and there was no base to fall back to; the issues still
 * come through so the card can say why. `opts.currency` is the account's
 * currency symbol (see defaultLook).
 */
export function resolveBlockCharts(block, opts) {
    const bases = baseChartsOf(block);
    const settings = Array.isArray(block?.config?.chartSettings) ? block.config.chartSettings : [];
    const rows = AGENT_SHAPED_TYPES.has(block?.type) ? block.result?.viz?.table : null;
    const table = Array.isArray(rows) && rows.length > 0 ? rows : null;
    const count = bases.length > 0 ? bases.length : (AGENT_SHAPED_TYPES.has(block?.type) && table && settings[0]?.data ? 1 : 0);
    const out = [];
    for (let i = 0; i < count; i++) {
        const entry = settings[i] || null;
        const { chart, issues } = resolveChart(bases[i] || null, table, entry, opts);
        out.push({ index: i, chart, issues, settings: entry, table });
    }
    return out;
}
